<template>
  <div class="rich-text-editor">
    <!-- CKEditor -->
    <ckeditor
      :editor="editor"
      :value="value"
      :config="editorConfig"
      :disabled="disabled"
      @input="onInput"

    ></ckeditor>

    <!-- Character Count -->
    <div class="editor-footer" v-if="showCharCount">
      <span class="char-count">{{ characterCount }} characters</span>
    </div>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

export default {
  name: 'RichTextEditor',
  components: {
    ckeditor: CKEditor.component
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Enter your message...'
    },
    showCharCount: {
      type: Boolean,
      default: true
    },
    maxLength: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {
        placeholder: this.placeholder,
        toolbar: {
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'link',
            'blockQuote',
            '|',
            'undo',
            'redo'
          ]
        },
        language: 'en'
      }
    }
  },
  computed: {
    characterCount() {
      if (!this.value) return 0

      // Count characters excluding HTML tags
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = this.value
      return tempDiv.textContent.length
    }
  },
  methods: {
    onInput(content) {
      if (this.disabled) return

      // Check max length if specified
      if (this.maxLength) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content
        const textLength = tempDiv.textContent.length

        if (textLength > this.maxLength) {
          return // Don't emit if over limit
        }
      }

      this.$emit('input', content)
    }
  }
}
</script>

<style lang="scss" scoped>
.rich-text-editor {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: white;
  
  &:focus-within {
    border-color: #a4393a;
    box-shadow: 0 0 0 0.125em rgba(164, 57, 58, 0.25);
  }
  
  // CKEditor styling
  ::v-deep .ck-editor {
    border: none;

    .ck-toolbar {
      border: none;
      border-bottom: 1px solid #e9ecef;
      background-color: #f8f9fa;
      border-radius: 0;

      .ck-button {
        &:hover {
          background-color: #e9ecef;
        }

        &.ck-on {
          background-color: #a4393a;
          color: white;

          &:hover {
            background-color: #8b2f30;
          }
        }
      }

      .ck-dropdown__button {
        &:hover {
          background-color: #e9ecef;
        }
      }
    }

    .ck-content {
      min-height: 120px;
      max-height: 300px;
      overflow-y: auto;
      padding: 0.75rem;
      font-size: 14px;
      line-height: 1.5;
      border: none;
      border-radius: 0;

      &:focus {
        border: none;
        box-shadow: none;
      }

      // Ensure LTR text direction
      direction: ltr;
      text-align: left;

      p {
        margin: 0 0 0.5rem 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul, ol {
        margin: 0 0 0.5rem 1.5rem;

        li {
          margin-bottom: 0.25rem;
        }
      }

      a {
        color: #a4393a;

        &:hover {
          color: #8b2f30;
        }
      }

      blockquote {
        border-left: 3px solid #a4393a;
        padding-left: 1rem;
        margin: 0 0 0.5rem 0;
        font-style: italic;
        color: #6c757d;
      }
    }
  }
  
  .editor-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
    font-size: 12px;
    color: #6c757d;
    
    .char-count {
      margin-left: auto;
    }
  }
}
</style>
